/**
 * OfficialForSection — the rosters this creator is on.
 *
 * The reverse view of the publisher's creator team. On a publisher page, "◆
 * Official · Modern Warfare 4" on every card restated the section heading and
 * the hero above it, so it was removed. HERE the same fact is the entire
 * content and the only place it is ever information: a working creator is on
 * several rosters at once — Call of Duty and Monster and Logitech — and no
 * other surface says so.
 *
 * The card is the publisher's Drop, never the StreamingTeam behind it. The team
 * is plumbing; nobody is official for "Call of Duty Creator Team".
 *
 * Renders nothing at all when the creator is on no rosters. Same rule the
 * engagement renderer and Game News follow — a heading over a gap is worse than
 * an absent section, and on a creator's own page an empty "Official for" would
 * read as a rejection rather than an absence.
 */
import React from "react";
import {useGetDropOfficialFor} from "@/hooks/drop/useGetDropOfficialFor";
import "./OfficialForSection.scss";

interface OfficialForEntry {
    dropId: string;
    slug: string;
    name: string;
    logoUrl?: string;
    memberCount: number;
}

interface OfficialForSectionProps {
    /** The creator Drop being viewed. */
    slug?: string;
    title?: string;
}

/** First letters, for a roster with no logo — same fallback the roster cards use. */
function initials(name: string): string {
    return name
        .split(/\s+/)
        .map(w => w[0] ?? "")
        .join("")
        .slice(0, 2)
        .toUpperCase() || "—";
}

export function OfficialForSection({slug, title = "Official for"}: OfficialForSectionProps): React.ReactElement | null {
    const {data, isLoading, isError} = useGetDropOfficialFor(slug);
    const entries: OfficialForEntry[] = data?.entries ?? [];

    /*
     * Nothing while loading, and nothing on error.
     *
     * This is a supporting section on someone else's page, so a skeleton or an
     * error box would both take up more room than the content is worth and draw
     * attention to a failure the visitor cannot act on. The section either has
     * something to say or it is not there.
     */
    if (isLoading || isError || entries.length === 0) return null;

    return (
        <section className="official-for" aria-label={title}>
            <div className="official-for__head">
                <h3 className="official-for__title">{title}</h3>
                <span className="official-for__note">
                    {entries.length === 1 ? "1 official roster" : `${entries.length} official rosters`}
                </span>
            </div>

            <div className="official-for__grid">
                {entries.map(e =>
                    /*
                     * Same tab. These are OUR pages and the visitor is already
                     * inside the product — unlike the publisher roster, where a
                     * new tab preserves a stream that is playing. There is
                     * nothing playing here to preserve.
                     */
                    <a key={e.dropId} className="official-for__card" href={`/drop-in/${e.slug}`}>
                        <span className="official-for__mark">
                            {e.logoUrl
                                ? <img src={e.logoUrl} alt="" loading="lazy" />
                                : <span aria-hidden="true">{initials(e.name)}</span>
                            }
                        </span>
                        <span className="official-for__meta">
                            <span className="official-for__name">{e.name}</span>
                            <span className="official-for__count">
                                {e.memberCount === 1 ? "1 creator" : `${e.memberCount} creators`}
                            </span>
                        </span>
                    </a>)}
            </div>
        </section>
    );
}
